import React, { useCallback, useContext, useMemo, useState } from 'react';
import type { StylesConfig } from 'react-select';

import styled from 'styled-components';

// constants
import aampeTheme from '../../constants/aampeTheme';
import ICONS from '../../constants/icons';

// context
import { EditorContext } from '../../context/EditorContext';

// hook
import useQueryFetchTemplates from '../../hooks/EmailTemplate/useQueryFetchTemplates';
import useRoleFlags from 'hooks/auth/useRoleFlags';

// models
import { EmailTemplates, FormulaStatus } from '../../models/formula.model';

// components
import SearchableSelect, {
    SearchableSelectType,
} from '../inputs/searchableSelect';
import AampeItalicText from '../shared/AampeText/AampeItalicText';
import FormulaTemplateModal from './FormulaTemplateMap/FormulaTemplateModal';

interface TemplateSelectorProps {
    onTemplateIdChange?: (templateID: string, isAampeCustom: boolean) => void;
    label: string;
}
const customStyles: StylesConfig<SearchableSelectType> = {
    container: (provided) => ({
        ...provided,
        width: '100%',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: aampeTheme.colors.gray700,
    }),
    option: (provided, state) => ({
        ...provided,
        cursor: 'pointer',
        backgroundColor:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.backgroundWG01
                : aampeTheme.colors.white,
        color:
            state.isFocused || state.isSelected
                ? aampeTheme.colors.gray700
                : aampeTheme.colors.neutral09,
        padding: aampeTheme.spacing(1, 2),
    }),
};
const FormulaTemplateSelector: React.FC<
    React.PropsWithChildren<TemplateSelectorProps>
> = ({ onTemplateIdChange, label }) => {
    const [temporaryValue, setTemporaryValue] =
        useState<SearchableSelectType>();
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const [isEmailTemplateContentMap, setIsEmailTemplateContentMap] =
        useState<boolean>(false);
    const { data: rawTemplates } = useQueryFetchTemplates();

    const { hasWriteAccess } = useRoleFlags();

    const isDisabled = useMemo<boolean>(() => {
        return !hasWriteAccess;
    }, [hasWriteAccess]);

    const templates = useMemo<EmailTemplates[]>(() => {
        return rawTemplates ?? [];
    }, [rawTemplates]);

    const templateOptions = useMemo<SearchableSelectType[]>(
        () =>
            templates?.map((template) => ({
                label: template.template_name,
                value: template.email_template_id,
                trailingIcon: template.is_aampe_custom_template ? (
                    <Image />
                ) : null,
            })) || [],
        [templates],
    );

    const isAampeCustomTemplate = useCallback<(id: string) => boolean>(
        (id: string) =>
            !!templates?.find((item) => item.email_template_id === id)
                ?.is_aampe_custom_template,
        [templates],
    );

    const selectedTemplate = useMemo<SearchableSelectType | undefined>(() => {
        return templateOptions.find(
            (option) =>
                option.value ===
                formula?.additional_metadata?.email_template_id,
        );
    }, [formula, templateOptions]);

    const handleTemplateChange = (value: SearchableSelectType) => {
        if (formula?.additional_metadata?.email_template_id) {
            setIsEmailTemplateContentMap(true);
            setTemporaryValue(value);
        } else {
            onTemplateIdChange?.(
                value.value as string,
                isAampeCustomTemplate(value.value as string),
            );
        }
    };

    const handleModalClose = () => {
        setIsEmailTemplateContentMap(false);
    };

    return (
        <>
            <Container>
                <TemplateTitleContainer>
                    <TemplateText>
                        {label} <AampeItalicText text="(Required)" />
                    </TemplateText>
                </TemplateTitleContainer>
                <TemplateSearchableSelect
                    name="template"
                    options={templateOptions}
                    onChange={(field, value) => {
                        handleTemplateChange(value as SearchableSelectType);
                    }}
                    placeholder="Start typing the template name"
                    customStyles={customStyles}
                    isDisabled={
                        formula?.status === FormulaStatus.Approved || isDisabled
                    }
                    value={selectedTemplate}
                />
            </Container>
            {isEmailTemplateContentMap && formula && (
                <FormulaTemplateModal
                    selectedMessages={[formula]}
                    templateOptions={templateOptions}
                    handleCancel={handleModalClose}
                    temporaryValue={temporaryValue as SearchableSelectType}
                    isAampeCustomEmailTemplate={isAampeCustomTemplate(
                        temporaryValue?.value as string,
                    )}
                    setTemporaryValue={setTemporaryValue}
                />
            )}
        </>
    );
};

export default FormulaTemplateSelector;
const Container = styled.div({
    display: 'flex',
    width: '100%',
    flexDirection: 'column',
    gap: aampeTheme.spacing(1),
    justifyContent: 'flex-start',
    flex: 1,
});
const TemplateTitleContainer = styled.div({
    display: 'flex',
    width: '100%',
});

const TemplateText = styled.span({
    fontSize: '16px',
    fontWeight: 500,
    lineHeight: '20px',
    color: aampeTheme.colors.gray800,
});

const TemplateSearchableSelect = styled(SearchableSelect)({
    width: '100%',
});

const Image = styled(ICONS.aampeLogo)({
    height: 16,
    width: 19,
});
